import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http'
import { inject } from '@angular/core'
import { Router } from '@angular/router'

import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs'

import { AuthService } from '@yoshu-lib/core/auth/auth.service'
import { AuthStore } from '@yoshu-lib/core/auth/auth.store'

import { ENVIRONMENT } from '../tokens/environment.token'

// Token refresh lock mechanism to prevent multiple simultaneous refresh requests
let isRefreshing = false
const refreshSubject = new BehaviorSubject<boolean>(false)

/**
 * An HTTP interceptor that handles authentication via HttpOnly cookies.
 *
 * - Adds withCredentials to all API requests so cookies are sent automatically
 * - Handles 401 errors by triggering a token refresh
 * - Uses a lock mechanism to ensure only one refresh happens at a time
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService)
  const authStore = inject(AuthStore)
  const router = inject(Router)
  const env = inject(ENVIRONMENT)

  const isYoshuApiRequest = req.url.startsWith(env.apiUrl)
  const isAuthRequest = req.url.includes('/auth/')

  // Only handle Yoshu API requests (non-auth endpoints)
  if (!isYoshuApiRequest || isAuthRequest) {
    return next(req)
  }

  // Add withCredentials so HttpOnly cookies are sent with the request
  const authReq = req.clone({ withCredentials: true })

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        return handleTokenRefresh(req, next, authService, authStore, router)
      }
      return throwError(() => error)
    })
  )
}

/**
 * Handles token refresh with a lock mechanism to prevent race conditions.
 * If a refresh is already in progress, waits for it to complete.
 */
function handleTokenRefresh(
  req: Parameters<HttpInterceptorFn>[0],
  next: Parameters<HttpInterceptorFn>[1],
  authService: AuthService,
  authStore: AuthStore,
  router: Router
) {
  if (isRefreshing) {
    // Wait for the ongoing refresh to complete
    return refreshSubject.pipe(
      filter((refreshed) => refreshed),
      take(1),
      switchMap(() => {
        // Retry the original request with credentials
        const retriedReq = req.clone({ withCredentials: true })
        return next(retriedReq)
      })
    )
  }

  // Start a new refresh
  isRefreshing = true
  refreshSubject.next(false)

  return authService.refreshAccessToken().pipe(
    switchMap(() => {
      isRefreshing = false
      refreshSubject.next(true)

      // Retry the original request - new access token cookie is already set by backend
      const retriedReq = req.clone({ withCredentials: true })
      return next(retriedReq)
    }),
    catchError((refreshError) => {
      isRefreshing = false
      refreshSubject.next(false)
      // Only call logout if user was logged in (to blacklist the refresh token)
      // If not logged in, just clear local state and redirect
      if (authStore.isLoggedIn()) {
        authService.logout().subscribe()
      } else {
        authStore.clearAuth()
        router.navigate(['/login'])
      }
      return throwError(() => refreshError)
    })
  )
}
